import { colors } from "../constants/colors";

export type Material = {
  order: number;
  name: string;
  label: string;
  price: number;
  description: string;
  colors: string[];
  defaultSwatch: string;
  outOfStock?: string[];
  outOfStockSize?: {
    [key: string]: string[];
  };
};

export type Materials = {
  [key: string]: Material;
};

export type HatStyle = {
  sizes: null | string[];
  materials: Materials;
};

export type HatStyles = {
  [key: string]: HatStyle;
};

const hatStyles: HatStyles = {
  snapback: {
    sizes: null,
    materials: {
      wool: {
        order: 1,
        name: "Wool",
        price: 85,
        label: "Wool - $85",
        description:
          "100% Wool for soft, breathable, year-round wearability. Our signature mid-profile fit with adjustable snapback. Made in the USA.",
        colors: colors.wool,
        defaultSwatch: "WOOL BLACK",
        outOfStock: ["WOOL NAVY", "WOOL SLUSH PUPPY"],
      },
      twoTone: {
        order: 2,
        name: "Two Tone",
        price: 90,
        label: "Two Tone - $90",
        description:
          "100% Wool for a soft, breathable, year-round wearability. Our signature mid-profile fit with an alternate color visor, eyelets and adjustable snapback. Classic Green twill undervisor. Made in the USA.",
        colors: colors.twoTone,
        // The following do not have a space after the slash: TWO TONE WOOL PINSTRIPE /BLACK, TWO TONE WOOL PINSTRIPE /ROYAL, TWO TONE WOOL PINSTRIPE /COLISEUM, TWO TONE WOOL PINSTRIPE  BALL FIELD
        defaultSwatch: "TWO TONE WOOL WHITE / CAMEL",
        outOfStock: ["TWO TONE WOOL WHITE / ROYAL", "TWO TONE WOOL PINSTRIPE /BLACK"],
      },
      twill: {
        order: 3,
        name: "Twill",
        price: 65,
        label: "Twill - $65",
        description:
          "100% Cotton cap in our signature mid-profile silhouette. Authentic fit and feel with adjustable snapback. Made in the USA.",
        colors: colors.twill,
        defaultSwatch: "TWILL SAND",
        outOfStock: ["TWILL WHITE", "TWILL BLACK", "TWILL NAVY", "TWILL SAND", "TWILL BONE"],
      },
      japaneseTwill: {
        order: 4,
        name: "Japanese Twill",
        price: 75,
        label: "Japanese Twill - $75",
        description:
          "100% Cotton. 3 Thread Twill. Our signature mid-profile fit with adjustable snapback. Made in the USA.",
        colors: colors.japaneseTwill,
        defaultSwatch: "JAPANESE TWILL ARMY",
        outOfStock: [
          "JAPANESE TWILL NAVY",
          "JAPANESE TWILL BLACK",
          "JAPANESE TWILL SAND",
          "JAPANESE TWILL ARMY",
          "JAPANESE TWILL WHITE",
        ],
      },
      japaneseTwillTwoTone: {
        order: 5,
        name: "Japanese Twill Two Tone",
        price: 80,
        label: "Japanese Twill Two Tone - $80",
        description:
          "100% cotton. Our signature mid-profile fit with adjustable snapback. Made in the USA.",
        colors: colors.japaneseTwillTwoTone,
        defaultSwatch: "JAPANESE TWILL TWO TONE BONE / BLACK",
        outOfStock: ["JAPANESE TWILL TWO TONE BONE / NAVY"],
      },
      "6WaleCorduroy": {
        order: 6,
        name: "6 Wale Corduroy",
        price: 80,
        label: "6 Wale Corduroy - $80",
        description:
          "100% combed mercerized cotton. Our signature mid-profile fit with adjustable snapback. Made in the USA.",
        colors: colors["6WaleCorduroy"],
        defaultSwatch: "6 WALE CORD GREY",
        outOfStock: ["6 WALE CORD MOCHA", "6 WALE CORD KHAKI", "6 WALE CORD BLACK"],
      },
      "14WaleCord": {
        order: 7,
        name: "14 Wale Cord",
        label: "14 Wale Cord - $85",
        price: 85,
        description:
          "100% Cotton. Our signature mid-profile fit with adjustable snapback. Made in the USA.",
        colors: colors["14WaleCord"],
        defaultSwatch: "14 WALE CORD BLACK",
        outOfStock: [""],
      },
      "21WaleCorduroy": {
        order: 8,
        name: "21 Wale Corduroy",
        price: 90,
        label: "21 Wale Corduroy - $90",
        description:
          "100% cotton. The finest most narrow wales. Our signature mid-profile fit with adjustable snapback. Made in the USA.",
        colors: colors["21WaleCorduroy"],
        defaultSwatch: "21 WALE CORD ROYAL",
        outOfStock: ["21 WALE CORD NAVY", "21 WALE CORD BONE"],
      },
      cashmere: {
        order: 9,
        name: "Cashmere",
        price: 120,
        label: "Cashmere - $120",
        description:
          "100% Cashmere Wool. Our most premium mid-profile cap has an adjustable snapback, and is finished with a Tonal Bridal Satin undervisor for a durable yet soft finish. Made in the USA.",
        colors: colors.cashmere,
        defaultSwatch: "CASHMERE CAMEL",
        outOfStock: ["CASHMERE BLACK"]
      },
      woolBlend: {
        order: 10,
        name: "Wool Blend",
        price: 65,
        label: "Wool Blend - $65",
        description:
          "15% Wool and 85% Acrylic for a more structured look in our signature mid profile fit with adjustable snapback. Made Overseas.",
        colors: colors.woolBlend,
        defaultSwatch: "WOOL BLEND FOREST",
      },
      woolBlend90: {
        order: 11,
        name: "Wool Blend",
        price: 90,
        label: "Wool Blend - $90",
        description:
          "15% Wool and 85% Acrylic for a more structured look in our signature mid profile fit with adjustable snapback. Made Overseas.",
        colors: colors.woolBlend,
        defaultSwatch: "WOOL BLEND FOREST",
      },
      canvas: {
        order: 12,
        name: "Canvas",
        price: 75,
        label: "Canvas - $75",
        description:
          "100% Cotton. Our signature mid-profile fit with adjustable snapback. Made in the USA.",
        colors: colors.canvas,
        defaultSwatch: "CANVAS CAMO",
        outOfStock: ["CANVAS OLIVE", "CANVAS NAVY"],
      },
      gabardine: {
        order: 13,
        name: "Gabardine",
        price: 90,
        label: "Gabardine - $90",
        description:
          "100% Australian Worsted Merino Wool. 2 ply yarn in the warp and filling so that the yarn is soft yet durable. Lighter weight for all seasons. Our signature mid-profile fit with adjustable snapback. Made in the USA.",
        colors: colors.gabardine,
        defaultSwatch: "GABARDINE RED",
        outOfStock: [
          "GABARDINE BLACK",
          "GABARDINE BALL FIELD",
          "GABARDINE NAVY",
          "GABARDINE WHITE",
        ],
      },
      velvet: {
        order: 14,
        name: "Velvet",
        price: 90,
        label: "Velvet - $90",
        description:
          "100% polyester. 8.75oz wt. Satin Forest Undervisor. Our signature mid-profile fit with adjustable snapback. Made in the USA.",
        colors: colors.velvet,
        defaultSwatch: "VELVET STOUT",
        outOfStock: ["VELVET NAVY", "VELVET BLACK"],
      },
      colorBlock: {
        order: 15,
        name: "Color Block",
        price: 90,
        label: "Color Block - $90",
        description:
          "100% wool. Our signature mid-profile fit with adjustable snapback. Made in the USA.",
        colors: colors.colorBlock,
        defaultSwatch: "COLOR BLOCK ROYAL / WHITE",
        outOfStock: [
          "COLOR BLOCK ROYAL / BONE / COLISEUM",
          "COLOR BLOCK COLISEUM / BONE / NAVY",
          "COLOR BLOCK NAVY / WHITE",
        ],
      },
      denim: {
        order: 16,
        name: "Denim",
        price: 75,
        label: "Denim - $75",
        description:
          "100% cotton. Our signature mid-profile fit with adjustable snapback. Made in the USA.",
        colors: colors.denim,
        defaultSwatch: "DENIM NARROW RAILROAD",
      },
      brushedTwill: {
        order: 17,
        name: "Brushed Twill",
        price: 75,
        label: "Brushed Twill - $75",
        description:
          "100% Cotton. Brushed Chino Twill on our signature mid-profile fit with adjustable snapback. Made in the USA.",
        colors: colors.brushedTwill,
        defaultSwatch: "BRUSHED TWILL BLACK",
        outOfStock: [],
      },
    },
  },
  snapbackCurved: {
    sizes: null,
    materials: {
      snapbackCurvedWool: {
        order: 1,
        name: "Snapback Curved Wool",
        price: 85,
        label: "Snapback Curved Wool - $85",
        description:
          "100% Wool for soft, breathable, year-round wearability. Our signature mid-profile fit with adjustable snapback and a curved visor. Made in the USA.",
        colors: colors.snapbackCurved,
        defaultSwatch: "SNAPBACK CURVED HIGHWAY",
        outOfStock: ["SNAPBACK CURVED NAVY"],
      },
    },
  },
  fitted: {
    sizes: ["7", "7 1/8", "7 1/4", "7 3/8", "7 1/2", "7 5/8", "7 3/4", "8", "8 1/4"],
    materials: {
      fittedWoolBlend: {
        order: 1,
        name: "Fitted Wool Blend",
        price: 80,
        label: "Fitted Wool Blend - $80",
        description:
          "15% Wool and 85% Acrylic for a more structured look in our signature mid profile fit, now available in Fitted. Made Overseas.",
        colors: colors.woolBlendFitted,
        defaultSwatch: "FITTED WOOL BLEND ROYAL",
        outOfStockSize: {
          "FITTED WOOL BLEND BLACK": ["7 5/8"],
        },
      },
      fittedMerinoWool: {
        order: 2,
        name: "Fitted Merino Wool",
        price: 125,
        label: "Fitted Merino Wool - $125",
        description:
          "The Courtside. 100% Australian Worsted Merino Wool. High Profile and Deep Crown. Green undervisor. Four row top stitched khaki sweatband. Made Overseas.",
        colors: colors.woolFitted,
        defaultSwatch: "FITTED MERINO WOOL BLACK",
      },
    },
  },
  dadHat: {
    sizes: null,
    materials: {
      cottonTwill: {
        order: 1,
        name: "Cotton Twill",
        price: 70,
        label: "Cotton Twill - $70",
        description:
          "100% cotton twill in our unstructured dad cap with a self fabric closure and gunmetal clasp. Made Overseas.",
        colors: colors.cottonTwill,
        defaultSwatch: "COTTON TWILL BONE",
        outOfStock: ["COTTON TWILL BLACK", "COTTON TWILL BONE", "COTTON TWILL FOREST"],
      },
      dadHatWool: {
        order: 2,
        name: "Dad Hat Wool",
        label: "Dad Hat Wool - $75",
        price: 75,
        description:
          "100% Wool in our unstructured dad cap with a self fabric closure, gunmetal clasp and grommet. Made in the USA.",
        colors: colors.dadHatWool,
        defaultSwatch: "DAD HAT WOOL FOREST",
        outOfStock: ["DAD HAT WOOL BLACK", "DAD HAT WOOL WHITE", "DAD HAT WOOL FOREST"],
      },
      "21WaleCordDad": {
        order: 3,
        name: "21 Wale Cord Dad",
        label: "21 Wale Cord Dad - $80",
        price: 80,
        description:
          "100% polyester in our unstructured dad cap with a self-fabric closure and gunmetal clasp. Made Overseas.",
        colors: colors["21WaleCordDad"],
        defaultSwatch: "21 WALE CORD DAD KHAKI",
        outOfStock: [""],
      },
    },
  },
  "5Panel": {
    sizes: null,
    materials: {
      "5PanelWool": {
        order: 1,
        name: "5 Panel Wool",
        label: "5 Panel Wool - $85",
        price: 85,
        description:
          "100% Wool with a single front panel on our mid-profile silhouette. Curved Visor. Made in the USA.",
        colors: colors.fivePanelWool,
        defaultSwatch: "5 PANEL WOOL BONE",
        outOfStock: ["5 PANEL WOOL BLACK", "5 PANEL WOOL NAVY", "5 PANEL WOOL BONE"],
      },
      "5PanelTwill": {
        order: 2,
        name: "5 Panel Twill",
        label: "5 Panel Twill - $75",
        price: 75,
        description:
          "100% Cotton Twill with a single front panel on our mid-profile silhouette. Curved Visor. Made in the USA.",
        colors: colors.fivePanelTwill,
        defaultSwatch: "5 PANEL TWILL BONE",
        outOfStock: ["5 PANEL TWILL NAVY", "5 PANEL TWILL BLACK", "5 PANEL TWILL BONE"],
      },
      "5PanelTrucker": {
        order: 3,
        name: "5 Panel Trucker",
        label: "5 Panel Trucker - $70",
        price: 70,
        description:
          "Cotton twill front panel with a mesh back in our mid-profile silhouette. Curved Visor. Made in the USA.",
        colors: colors.fivePanelTrucker,
        defaultSwatch: "5 PANEL TRUCKER BONE",
        outOfStock: ["5 PANEL TRUCKER NAVY", "5 PANEL TRUCKER BLACK", "5 PANEL TRUCKER BONE"],
      },
      "5PanelBrushedTwill": {
        order: 4,
        name: "5 Panel Brushed Twill",
        label: "5 Panel Brushed Twill - $75",
        price: 75,
        description:
          "100% Cotton. Brushed Chino Twill with a single front panel on our mid-profile silhouette. Curved Visor. Made in the USA.",
        colors: colors.fivePanelBrushedTwill,
        defaultSwatch: "5 PANEL BRUSHED TWILL BLACK",
        outOfStock: [
          "5 PANEL BRUSHED TWILL BONE",
          "5 PANEL BRUSHED TWILL ROYAL",
          "5 PANEL BRUSHED TWILL NAVY",
          "5 PANEL BRUSHED TWILL BLACK",
          "5 PANEL BRUSHED TWILL FOREST",
        ],
      },
      "5Panel14WaleCord": {
        order: 5,
        name: "5 Panel 14 Wale Cord",
        label: "5 Panel 14 Wale Cord - $85",
        price: 85,
        description:
          "100% Cotton with a single front panel on our mid-profile silhouette. Curved Visor. Made in the USA.",
        colors: colors["5Panel14WaleCord"],
        defaultSwatch: "5 PANEL 14 WALE CORD ROYAL",
        outOfStock: [
          "5 PANEL 14 WALE CORD BLACK",
          "5 PANEL 14 WALE CORD BONE",
          "5 PANEL 14 WALE CORD NAVY",
          "5 PANEL 14 WALE CORD BUD",
        ],
      },
    },
  },
  knitBeanies: {
    sizes: null,
    materials: {
      merinoWoolBeanie: {
        order: 1,
        name: "Merino Wool Beanie",
        label: "Merino Wool Beanie - $80",
        price: 80,
        description:
          "100% Merino Wool for soft, breathable, year-round wearability. Ribbed texture with a comfortable shape. Made in the USA.",
        colors: colors.merinoWoolBeanie,
        defaultSwatch: "MERINO WOOL BEANIE BLACK",
        outOfStock: [],
      },
    },
  },
};

export default hatStyles;
